import { useState, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import JudgmentModal from './JudgmentModal';

interface ReportRendererProps {
  markdown: string;
  className?: string;
}

// ISAP code to full ISAP ID mapping
// Format: WDU + YEAR(4) + ISSUE(3) + POSITION(4)
const ISAP_CODES: Record<string, { isapId: string; name: string }> = {
  KC: { isapId: 'WDU19640160093', name: 'Kodeks cywilny' },                          // Dz.U. 1964 Nr 16 poz. 93
  KPC: { isapId: 'WDU19640430296', name: 'Kodeks postępowania cywilnego' },          // Dz.U. 1964 Nr 43 poz. 296
  KK: { isapId: 'WDU19970880553', name: 'Kodeks karny' },                            // Dz.U. 1997 Nr 88 poz. 553
  KPK: { isapId: 'WDU19970890555', name: 'Kodeks postępowania karnego' },            // Dz.U. 1997 Nr 89 poz. 555
  KP: { isapId: 'WDU19740240141', name: 'Kodeks pracy' },                            // Dz.U. 1974 Nr 24 poz. 141
  KPA: { isapId: 'WDU19600300168', name: 'Kodeks postępowania administracyjnego' },  // Dz.U. 1960 Nr 30 poz. 168
  KRO: { isapId: 'WDU19640090059', name: 'Kodeks rodzinny i opiekuńczy' },           // Dz.U. 1964 Nr 9 poz. 59
  KSH: { isapId: 'WDU20000941037', name: 'Kodeks spółek handlowych' },               // Dz.U. 2000 Nr 94 poz. 1037
  KONSTYTUCJA: { isapId: 'WDU19970780483', name: 'Konstytucja RP' },                 // Dz.U. 1997 Nr 78 poz. 483
  KW: { isapId: 'WDU19991010991', name: 'Kodeks wykroczeń' },                        // Dz.U. 1999 Nr 101 poz. 991 (tekst jednolity)
  PPP: { isapId: 'WDU20090150096', name: 'Prawo prywatne międzynarodowe' },          // Dz.U. 2009 Nr 15 poz. 96 (nowa ustawa)
};

// Build ISAP URL from code and article
function buildISAPUrl(code: string, article: string): string {
  const codeInfo = ISAP_CODES[code.toUpperCase()];
  if (codeInfo) {
    return `https://isap.sejm.gov.pl/isap.nsf/DocDetails.xsp?id=${codeInfo.isapId}`;
  }
  // Fallback to search
  return `https://isap.sejm.gov.pl/isap.nsf/search.xsp?searchPhrase=${encodeURIComponent(`Art. ${article} ${code}`)}`;
}

// Custom URL transform that allows our custom protocols (judgment:, isap:)
function customUrlTransform(url: string): string {
  // Allow our custom protocols
  if (url.startsWith('judgment:') || url.startsWith('isap:')) {
    return url;
  }
  // Allow standard protocols
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('mailto:')) {
    return url;
  }
  // Allow relative URLs
  if (url.startsWith('/') || url.startsWith('#') || url.startsWith('./') || url.startsWith('../')) {
    return url;
  }
  // Block other protocols (javascript:, data:, etc.)
  return '';
}

export default function ReportRenderer({ markdown, className = '' }: ReportRendererProps) {
  const [selectedJudgmentId, setSelectedJudgmentId] = useState<number | null>(null);

  const handleJudgmentClick = useCallback((judgmentId: number) => {
    setSelectedJudgmentId(judgmentId);
  }, []);

  const closeModal = useCallback(() => {
    setSelectedJudgmentId(null);
  }, []);

  // Custom components for ReactMarkdown
  const components: Components = useMemo(() => ({
    // Custom link renderer - note: we don't spread props to avoid react-markdown's node prop
    a: ({ href, children }) => {
      if (!href) {
        return <span>{children}</span>;
      }

      // Handle judgment links: judgment:12345
      if (href.startsWith('judgment:')) {
        const judgmentId = parseInt(href.replace('judgment:', ''), 10);
        if (!isNaN(judgmentId)) {
          return (
            <button
              type="button"
              onClick={() => handleJudgmentClick(judgmentId)}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors font-medium text-sm"
              title="Kliknij aby zobaczyć orzeczenie"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {children}
            </button>
          );
        }
      }

      // Handle ISAP links: isap:KC:415
      if (href.startsWith('isap:')) {
        const parts = href.replace('isap:', '').split(':');
        if (parts.length >= 2) {
          const code = parts[0];
          const article = parts[1];
          const isapUrl = buildISAPUrl(code, article);
          const codeInfo = ISAP_CODES[code.toUpperCase()];

          return (
            <a
              href={isapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors font-medium text-sm"
              title={codeInfo ? `Otwórz ${codeInfo.name} w ISAP` : 'Otwórz w ISAP'}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              {children}
              <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          );
        }
      }

      // Regular external link
      if (href.startsWith('http')) {
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            {children}
          </a>
        );
      }

      // Default link
      return (
        <a href={href} className="text-blue-600 dark:text-blue-400 hover:underline">
          {children}
        </a>
      );
    },

    // Style headings - don't spread props to avoid react-markdown's node prop breaking nested elements
    h1: ({ children }) => (
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white mt-6 mb-4 pb-2 border-b border-slate-200 dark:border-slate-700">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mt-6 mb-3">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-lg font-medium text-slate-700 dark:text-slate-200 mt-4 mb-2">
        {children}
      </h3>
    ),

    // Style paragraphs
    p: ({ children }) => (
      <p className="text-slate-700 dark:text-slate-300 mb-3 leading-relaxed">
        {children}
      </p>
    ),

    // Style blockquotes
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-blue-400 dark:border-blue-600 pl-4 py-2 my-3 bg-blue-50 dark:bg-blue-900/20 rounded-r italic text-slate-600 dark:text-slate-400">
        {children}
      </blockquote>
    ),

    // Style lists
    ul: ({ children }) => (
      <ul className="list-disc list-inside space-y-1 mb-3 text-slate-700 dark:text-slate-300">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal list-inside space-y-1 mb-3 text-slate-700 dark:text-slate-300">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="ml-2">
        {children}
      </li>
    ),

    // Style strong/bold - critical: don't spread props or nested links break
    strong: ({ children }) => (
      <strong className="font-semibold text-slate-900 dark:text-white">
        {children}
      </strong>
    ),

    // Style horizontal rules
    hr: () => (
      <hr className="my-6 border-slate-200 dark:border-slate-700" />
    ),

    // Style code blocks
    code: ({ children, className: codeClassName }) => {
      const isInline = !codeClassName;
      if (isInline) {
        return (
          <code className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-sm font-mono text-slate-800 dark:text-slate-200">
            {children}
          </code>
        );
      }
      return (
        <code className={`block p-3 bg-slate-100 dark:bg-slate-800 rounded-lg text-sm font-mono overflow-x-auto ${codeClassName}`}>
          {children}
        </code>
      );
    },
  }), [handleJudgmentClick]);

  return (
    <>
      <div className={`report-content ${className}`}>
        <ReactMarkdown components={components} urlTransform={customUrlTransform}>
          {markdown}
        </ReactMarkdown>
      </div>

      {/* Judgment Modal */}
      {selectedJudgmentId !== null && (
        <JudgmentModal
          judgmentId={selectedJudgmentId}
          onClose={closeModal}
        />
      )}
    </>
  );
}
